/*
 * Eterna — country and language menu (snippets/et-locale-menu.liquid), 01.10.2026.
 *
 * The menu works without this file: every option is a submit button of the stock localization form.
 * The script adds the A–Z order in the page language (Shopify sorts countries by a Latin transliteration),
 * the country search, focus handling for the stock dropdown and drawer wrappers, the memory of a manual
 * language choice (silences snippets/et-lang-suggest) and an analytics event.
 *
 * The header section re-renders its mobile drawer after load (section hydration). The element carries
 * data-skip-subtree-update so the sorted list survives, and it reads the list from the DOM on every use
 * rather than caching nodes.
 */
class EtLocaleForm extends HTMLElement {
  #ready = false;
  #sorted = false;

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;

    this.#sort();
    // The list flags wait for the menu: the first hover, touch or focus on its wrapper (just before it opens) or the opening itself.
    const wrapper = this.closest('.et-lm');
    if (wrapper) {
      for (const type of ['pointerenter', 'touchstart', 'focusin']) {
        wrapper.addEventListener(type, this.#loadFlags, { once: true, passive: true });
      }
    }
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onClick);
  }

  /** @returns {HTMLElement | null} The country list. */
  get #list() {
    return this.querySelector('[data-et-lm-countries]');
  }

  /** @returns {HTMLInputElement | null} The country search field. */
  get #search() {
    return this.querySelector('[data-et-lm-search]');
  }

  /** @returns {HTMLElement[]} The country list items, in their current order. */
  get #items() {
    const list = this.#list;
    return list ? /** @type {HTMLElement[]} */ (Array.from(list.children)) : [];
  }

  /**
   * Called by the stock wrappers once the panel is open. In the header it moves focus into the panel;
   * in the mobile menu it does nothing, so the keyboard does not cover the lists.
   */
  focusSearchInput = () => {
    this.#loadFlags();
    this.#sort();
    if (this.dataset.mode !== 'dropdown') return;

    const target = this.querySelector('.et-lm__langs [aria-current="true"]') ?? this.querySelector('.et-lm__opt');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  };

  /**
   * Shows the list flags. They are not in the page's initial downloads (21.10.2026, speed); the URL waits in data-et-lm-flag.
   */
  #loadFlags = () => {
    for (const flag of this.querySelectorAll('[data-et-lm-flag]')) {
      if (!(flag instanceof HTMLElement)) continue;
      flag.style.backgroundImage = `url(${flag.dataset.etLmFlag})`;
      flag.removeAttribute('data-et-lm-flag');
    }
  };

  /**
   * Called by the stock wrappers when the panel closes.
   */
  resetForm = () => {
    const search = this.#search;
    if (!search || search.value === '') return;

    search.value = '';
    this.#filter();
  };

  /**
   * Puts the current country first and the rest in the alphabetical order of the page language.
   */
  #sort() {
    const list = this.#list;
    const items = this.#items;
    if (this.#sorted || !list || items.length < 2) return;
    this.#sorted = true;

    /** @type {(a: string, b: string) => number} */
    let compare;
    try {
      compare = new Intl.Collator(this.dataset.locale || undefined, { sensitivity: 'base' }).compare;
    } catch (error) {
      compare = (a, b) => a.localeCompare(b);
    }

    const rank = (/** @type {Element} */ item) => (item.querySelector('[aria-current="true"]') ? 0 : 1);
    items.sort((a, b) => rank(a) - rank(b) || compare(this.#name(a), this.#name(b)));
    list.append(...items);
  }

  /**
   * @param {Element} item - A country list item.
   * @returns {string} The country name as shown.
   */
  #name(item) {
    return item.querySelector('.et-lm__name')?.textContent?.trim() ?? '';
  }

  /**
   * @param {string | null | undefined} value - Text to compare.
   * @returns {string} Lower-case text without diacritics and apostrophes.
   */
  #normalize(value) {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/['’ʼ`]/g, '')
      .trim();
  }

  /**
   * A country matches when one of the words of its name (or of its aliases) starts with the query,
   * or when the query is its ISO code. "ні" finds Нідерланди and Німеччина, not Данія.
   *
   * @param {HTMLElement} item - A country list item.
   * @param {string} query - The normalized search text.
   * @returns {boolean} Whether the country should stay visible.
   */
  #matches(item, query) {
    if ((item.dataset.code ?? '').toLowerCase() === query) return true;

    item.dataset.haystack ??= this.#normalize(`${this.#name(item)} ${item.dataset.aliases ?? ''}`);
    const haystack = item.dataset.haystack;

    return (
      haystack.split(/[\s,()/–—-]+/).some((word) => word.startsWith(query)) ||
      (query.includes(' ') && haystack.includes(query))
    );
  }

  #filter() {
    const search = this.#search;
    const list = this.#list;
    if (!search || !list) return;

    const query = this.#normalize(search.value);
    let visible = 0;

    for (const item of this.#items) {
      const match = query === '' || this.#matches(item, query);
      item.hidden = !match;
      if (match) visible++;
    }

    const empty = this.querySelector('[data-et-lm-empty]');
    if (empty instanceof HTMLElement) empty.hidden = visible > 0;

    const live = this.querySelector('[data-et-lm-live]');
    if (live && this.dataset.labelResults) {
      live.textContent = query === '' ? '' : this.dataset.labelResults.replace('[count]', String(visible));
    }
    list.scrollTop = 0;
  }

  /**
   * @returns {HTMLButtonElement[]} The buttons of the countries that match the search.
   */
  #visibleButtons() {
    return this.#items
      .filter((item) => !item.hidden)
      .map((item) => item.querySelector('button'))
      .filter((button) => button instanceof HTMLButtonElement);
  }

  /**
   * @param {Event} event - The event object.
   */
  #onInput = (event) => {
    if (event.target !== this.#search) return;

    this.#sort();
    this.#filter();
  };

  /**
   * @param {KeyboardEvent} event - The event object.
   */
  #onKeyDown = (event) => {
    const search = this.#search;

    if (event.target === search) {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (search?.value.trim()) this.#visibleButtons()[0]?.click();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.#visibleButtons()[0]?.focus();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const buttons = this.#visibleButtons();
    const index = buttons.findIndex((button) => button === document.activeElement);
    if (index === -1) return;

    event.preventDefault();
    const next = event.key === 'ArrowDown' ? buttons[index + 1] : buttons[index - 1] ?? search;
    next?.focus();
  };

  /**
   * @param {MouseEvent} event - The event object.
   */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    // The current option: nothing to change, close the panel instead of reloading the page.
    if (button.getAttribute('aria-current') === 'true') {
      event.preventDefault();
      const dropdown = this.closest('dropdown-localization-component');
      if (dropdown && 'hidePanel' in dropdown && typeof dropdown.hidePanel === 'function') dropdown.hidePanel();
      return;
    }

    const type = button.name === 'language_code' ? 'language' : 'country';

    if (type === 'language') {
      // Same record as snippets/et-lang-suggest keeps after a manual choice in the selector.
      try {
        localStorage.setItem('et_lang_suggest', JSON.stringify({ a: 'manual', l: button.value, t: Date.now() }));
      } catch (error) {
        // Storage can be unavailable (private mode); the menu still works.
      }
    }

    try {
      window.Shopify?.analytics?.publish?.('et_locale_menu', {
        type,
        from: type === 'language' ? this.dataset.locale : this.dataset.country,
        to: button.value,
        place: this.dataset.mode,
      });
    } catch (error) {
      // Analytics must never block the switch.
    }
  };
}

if (!customElements.get('et-locale-form')) {
  customElements.define('et-locale-form', EtLocaleForm);
}
